import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Axios from "axios";
import AxiosInstance from "../../AxiosInstance";
import StateContext from "../../Contexts/StateContext";
import { 
  Grid, Typography, CircularProgress, Container, Box, 
  Button, Card, Divider, Avatar, TextField, Snackbar, Alert, Chip,
  Autocomplete, Paper
} from "@mui/material";
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';

// Utils
import { formatPhoneNumber } from "../../Utils/PhoneUtils";

import defaultProfilePicture from "../../Assets/defaultProfilePicture.jpg";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ChatIcon from '@mui/icons-material/Chat';
import SpeedIcon from '@mui/icons-material/Speed';
import ConstructionIcon from '@mui/icons-material/Construction';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import StarIcon from '@mui/icons-material/Star';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';

const defaultIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});

function MasterDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const GlobalState = useContext(StateContext);
  const [master, setMaster] = useState(null);
  const [allMasters, setAllMasters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [openSnack, setOpenSnack] = useState(false);
  const [bookingStatus, setBookingStatus] = useState('success');
  const [linkMasterId, setLinkMasterId] = useState(null);
  const { config, environment } = require('../../index');

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function fetchData() {
      try {
        const response = await AxiosInstance.get(`${config[environment].businessesEndpoint}${id}/`, { cancelToken: source.token });
        setMaster(response.data);
        
        // If user might be admin, fetch all masters for linking
        const allRes = await AxiosInstance.get(`${config[environment].businessesEndpoint}?type=Master`, { cancelToken: source.token });
        setAllMasters(allRes.data.filter(m => !m.is_recommended && m.id !== parseInt(id)));
        
        setLoading(false);
      } catch (error) {
        if (!Axios.isCancel(error)) {
          setLoading(false);
        }
      }
    }
    fetchData();
    return () => source.cancel();
  }, [id, config, environment]);

  const handleLinkMaster = async () => {
    try {
      await AxiosInstance.patch(`${config[environment].businessesEndpoint}${id}/`, {
        linked_master: linkMasterId
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setBookingStatus('success');
      setOpenSnack(true);
      // Refresh data
      const response = await AxiosInstance.get(`${config[environment].businessesEndpoint}${id}/`);
      setMaster(response.data);
    } catch (error) {
      setBookingStatus('error');
      setOpenSnack(true);
    }
  };

  const handleBooking = async () => {
    try {
      const response = await AxiosInstance.post(config[environment].businessBookingsEndpoint, {
        business: id,
        date: selectedDate.format('YYYY-MM-DD'),
        message: t('Business.BookingFromWebsite', "Foglalás a weboldalról")
      }, {
        headers: {
          Authorization: `Bearer ${GlobalState.jwtAccessToken}`
        }
      });
      setBookingStatus('success');
      setOpenSnack(true);
    } catch (error) {
      setBookingStatus('error');
      setOpenSnack(true);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(t('Common.DeleteConfirm'))) {
      try {
        try {
          await AxiosInstance.delete(`${config[environment].businessesEndpoint}${id}/delete/`);
        } catch (delErr) {
          if (delErr.response && (delErr.response.status === 405 || delErr.response.status === 404)) {
            await AxiosInstance.delete(`${config[environment].businessesEndpoint}${id}/`);
          } else {
            throw delErr;
          }
        }
        setBookingStatus('success');
        setOpenSnack(true);
        setTimeout(() => {
          if (master.business_type === 'Master') {
            navigate(`/${i18n.language}/mesterek`);
          } else if (master.business_type === 'DayLabourer') {
            navigate(`/${i18n.language}/napszamosok`);
          } else {
            navigate(`/${i18n.language}/cegek`);
          }
        }, 2000);
      } catch (error) {
        setBookingStatus('error');
        setOpenSnack(true);
      }
    }
  };

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ height: "100vh" }}>
        <CircularProgress />
      </Grid>
    );
  }

  if (!master) return <Typography align="center" sx={{ mt: 5 }}>{t('Business.MasterNotFound', 'Master not found')}</Typography>;

  const getWhatsAppUrl = (phone) => {
    if (!phone) return null;
    return `https://wa.me/${phone.replace(/\D/g, '')}`;
  };

  const whatsappUrl = getWhatsAppUrl(master.whatsapp_number || master.phone_number);
  const messengerUrl = master.facebook_url ? master.facebook_url.replace('facebook.com', 'm.me') : null;

  const shouldDisableDate = (date) => {
    if (!master || !master.availabilities) return false;
    const dateStr = date.format('YYYY-MM-DD');
    const availability = master.availabilities.find(a => a.date === dateStr);
    return availability ? !availability.is_available : false;
  };

  const isRecommended = master ? !!(
    master.is_recommended === true || 
    master.is_recommended === 1 || 
    master.is_recommended === '1' || 
    master.is_recommended === 'true'
  ) : false;

  const isOwner = master && GlobalState.userId && parseInt(master.owner) === parseInt(GlobalState.userId);

  const editPath = master ? (
    master.business_type === 'Master' ? `/${i18n.language}/mester/szerkesztes/${id}` :
    master.business_type === 'Company' ? `/${i18n.language}/ceg/szerkesztes/${id}` :
    `/${i18n.language}/napszamos/szerkesztes/${id}`
  ) : `/${i18n.language}/mester/szerkesztes/${id}`;

  return (
    <Container maxWidth="lg" sx={{ py: 4, mt: '50px' }}>
      <Button onClick={() => navigate(-1)} sx={{ mb: 2 }}>{t('Common.Back')}</Button>
      
      {isOwner && (
        <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => navigate(editPath)}
          >
            {t('Common.Edit')}
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDelete}
          >
            {t('Common.Delete')}
          </Button>
        </Box>
      )}

      <Grid container spacing={4}>
        <Grid item xs={12} md={5}>
          <Card sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5', height: '100%' }}>
            <Avatar 
              src={master.picture || defaultProfilePicture} 
              sx={{ width: 150, height: 150, mx: 'auto', mb: 2, border: '3px solid #105b79' }}
            />
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#105b79' }}>
              {master.name}
            </Typography>
            {(master.category || master.description) && (
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                {master.category ? (master.category.startsWith('Business.') ? t(master.category) : master.category) : 
                 (master.description && master.description.length < 50 ? master.description : '')}
              </Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', my: 2 }}>
              {isRecommended && (
                <Chip icon={<StarIcon />} label={t('Business.Recommended')} color="primary" variant="filled" />
              )}
              {master.is_urgent && (
                <Chip icon={<SpeedIcon />} label={t('Business.UrgentNow', 'Sürgős most')} color="error" variant="filled" />
              )}
              {master.hourly_rate && (
                <Chip icon={<AttachMoneyIcon />} label={`${master.hourly_rate} RON/óra`} variant="outlined" />
              )}
              {master.fixed_price && (
                <Chip icon={<AttachMoneyIcon />} label={`${master.fixed_price} RON fix`} variant="outlined" />
              )}
              <Chip 
                icon={<ConstructionIcon />} 
                label={master.brings_tools ? t('Business.BringsTools', 'Hoz szerszámot') : t('Business.NoTools', 'Nem hoz szerszámot')} 
                variant="outlined" 
              />
            </Box>

            <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
              {master.description || t('Common.NoBio', 'Nincs leírás megadva.')}
            </Typography>

            {isRecommended && (
              <Alert severity="info" icon={<StarIcon />} sx={{ mb: 2, textAlign: 'left' }}>
                {t('Business.RecommendationNote')}
                {master.recommended_by_name && (
                   <Box component="span" sx={{ display: 'block', mt: 1, fontWeight: 'bold' }}>
                     {t('Business.RecommendedBy')} {master.recommended_by_name}
                   </Box>
                )}
              </Alert>
            )}

            {master.linked_master_details && (
              <Button 
                variant="outlined" 
                startIcon={<PersonIcon />} 
                onClick={() => navigate(`/mester/${master.linked_master_details.id}`)}
                fullWidth
                sx={{ mb: 2 }}
              >
                {t('Business.ViewRegisteredProfile')}
              </Button>
            )}
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, my: 2, textAlign: 'left' }}>
              {master.phone_number && (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <strong>{t('Administration.PhoneNumber')}:</strong> {formatPhoneNumber(master.phone_number)}
                </Typography>
              )}
              {master.email && (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <strong>{t('Administration.Email')}:</strong> {master.email}
                </Typography>
              )}
              {master.website && (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <strong>{t('Business.Website', 'Honlap')}:</strong> <a href={master.website.startsWith('http') ? master.website : `https://${master.website}`} target="_blank" rel="noopener noreferrer">{master.website}</a>
                </Typography>
              )}
              {master.szek && (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <strong>{t('Events.Szek')}:</strong> {master.szek}
                </Typography>
              )}
              {master.kozsegvaros && (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <strong>{t('Events.Kozseg')}:</strong> {master.kozsegvaros}
                </Typography>
              )}
              {master.telepules && (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <strong>{t('Events.Telepules')}:</strong> {master.telepules}
                </Typography>
              )}
              {master.address && (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <strong>{t('Thematic.Address')}:</strong> {master.address}
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {whatsappUrl && (
                <Button 
                  variant="contained" 
                  color="success" 
                  startIcon={<WhatsAppIcon />}
                  href={whatsappUrl}
                  target="_blank"
                >
                  WhatsApp
                </Button>
              )}
              {messengerUrl && (
                <Button 
                  variant="contained" 
                  sx={{ bgcolor: '#0084FF', '&:hover': { bgcolor: '#0073e6' } }}
                  startIcon={<ChatIcon />}
                  href={messengerUrl}
                  target="_blank"
                >
                  Messenger
                </Button>
              )}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          {(master.latitude && master.longitude) && (
            <Card sx={{ mb: 3, p: 2 }}>
              <Typography variant="h6" gutterBottom>{t('Common.Location')}</Typography>
              <Box sx={{ height: '300px', width: '100%', borderRadius: '8px', overflow: 'hidden' }}>
                <MapContainer 
                  center={[master.latitude, master.longitude]} 
                  zoom={13} 
                  scrollWheelZoom={false} 
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer url={config[environment].osmTiles} />
                  <Marker position={[master.latitude, master.longitude]} icon={defaultIcon} />
                </MapContainer>
              </Box>
            </Card>
          )}

          <Card sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
              {t('Business.BookMaster', 'Mester foglalása')}
            </Typography>
            
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ mb: 3, opacity: isRecommended ? 0.6 : 1, pointerEvents: isRecommended ? 'none' : 'auto' }}>
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue)}
                  renderInput={(params) => <TextField {...params} />}
                  minDate={dayjs()}
                  shouldDisableDate={shouldDisableDate}
                  readOnly={isRecommended}
                />
              </Box>
            </LocalizationProvider>

            <Box sx={{ mt: 2, textAlign: 'right' }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t('Business.SelectedDate', 'Kiválasztott dátum')}: {selectedDate.format('YYYY-MM-DD')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
                {isRecommended && (
                  <Typography variant="body2" color="error" sx={{ fontWeight: 'bold' }}>
                    {t('Business.BookingDisabledForRecommended', 'A foglalás csak regisztrált mesterek számára elérhető.')}
                  </Typography>
                )}
                <Button 
                  variant="contained" 
                  size="large" 
                  onClick={handleBooking}
                  sx={{ bgcolor: '#105b79' }}
                  disabled={isRecommended}
                >
                  {t('Business.ConfirmBooking', 'Foglalás megerősítése')}
                </Button>
              </Box>
            </Box>
            
            {isRecommended && (
              <Box sx={{ mt: 4, p: 2, textAlign: 'center', border: '2px dashed #ccc', bgcolor: '#fafafa', borderRadius: 2 }}>
                <PersonIcon sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                  {t('Business.NotRegisteredNote', { name: master.name })}
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  sx={{ mt: 2, color: 'var(--sznodarkblue)', borderColor: 'var(--sznodarkblue)' }}
                  onClick={() => navigate(`/${i18n.language}/uzleti-regisztracio`)}
                >
                  {t('Business.RegisterAsMaster')}
                </Button>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={openSnack} autoHideDuration={6000} onClose={() => setOpenSnack(false)}>
        <Alert onClose={() => setOpenSnack(false)} severity={bookingStatus} sx={{ width: '100%' }}>
          {bookingStatus === 'success' 
            ? (t('Common.DeleteSuccess') ? t('Common.DeleteSuccess') : t('Common.Success'))
            : (t('Common.DeleteError') ? t('Common.DeleteError') : t('Common.Error'))}
        </Alert>
      </Snackbar>

      {/* Admin section for linking masters */}
      {isRecommended && !master.linked_master && (
        <Paper sx={{ p: 3, mt: 4, bgcolor: '#fffde7' }}>
          <Typography variant="h6" gutterBottom color="warning.dark">
            <LinkIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            {t('Business.LinkRegisteredMaster', 'Regisztrált mester társítása')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('Business.LinkMasterDescription', 'Ha ez az ajánlott mester már regisztrált az oldalra, itt tudod összekapcsolni a két profilt.')}
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <Autocomplete
                options={allMasters.sort((a, b) => a.name.localeCompare(b.name, i18n.language))}
                getOptionLabel={(option) => `${option.name} (${option.category && option.category.startsWith('Business.') ? t(option.category) : (option.category || '')})`}
                onChange={(event, newValue) => {
                  setLinkMasterId(newValue ? newValue.id : null);
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t('Business.SelectMaster', 'Válassz mestert')} size="small" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button 
                variant="contained" 
                color="warning" 
                fullWidth 
                onClick={handleLinkMaster}
                disabled={!linkMasterId}
              >
                {t('Common.Submit')}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Container>
  );
}

export default MasterDetail;